import { FC } from 'react';
import { ConversationListItem } from '@/types/agents';

interface ConversationSidebarProps {
  conversations: ConversationListItem[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onNew: () => void;
  loading?: boolean;
  isMobileOpen?: boolean;
  onMobileClose?: () => void;
}

function formatRelative(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  const diff = Date.now() - date.getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `${min} min`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} h`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day} d`;
  return date.toLocaleDateString('pt-BR');
}

const ConversationSidebar: FC<ConversationSidebarProps> = ({
  conversations,
  activeId,
  onSelect,
  onNew,
  loading,
  isMobileOpen = false,
  onMobileClose,
}) => {
  return (
    <>
      {/* Overlay mobile — fecha o drawer ao clicar fora */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onMobileClose}
          aria-hidden="true"
        />
      )}

      {/* Em desktop é uma coluna fixa; em mobile, drawer deslizando da direita */}
      <aside
        data-testid="conversation-sidebar"
        className={`w-[280px] border-l border-surface-border bg-surface-secondary flex flex-col h-full z-50 transition-transform duration-300 fixed right-0 top-0 bottom-0 lg:relative lg:right-auto lg:top-auto lg:bottom-auto lg:translate-x-0 ${
          isMobileOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'
        }`}
      >
        {/* Botão fechar — só no mobile */}
        <div className="lg:hidden flex justify-end p-2 border-b border-surface-border">
          <button
            type="button"
            onClick={onMobileClose}
            aria-label="Fechar conversas"
            className="p-2 hover:bg-surface-hover rounded-lg"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <div className="p-4 border-b border-surface-border">
          <button
            type="button"
            data-testid="new-conversation"
            onClick={onNew}
            className="w-full flex items-center justify-center gap-2 rounded-xl bg-accent text-surface-primary font-semibold py-2.5 hover:bg-accent/90 transition"
          >
            <span className="material-symbols-outlined">add</span>
            Nova conversa
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {loading && <div className="p-4 text-sm text-content-tertiary">Carregando…</div>}
          {!loading && conversations.length === 0 && (
            <div className="p-4 text-sm text-content-tertiary">Nenhuma conversa ainda.</div>
          )}
          {conversations.map((c) => {
            const active = c.id === activeId;
            return (
              <button
                key={c.id}
                type="button"
                data-testid="conversation-item"
                data-active={active}
                onClick={() => onSelect(c.id)}
                className={`w-full text-left px-3 py-2.5 rounded-lg mb-1 transition ${
                  active
                    ? 'bg-accent/10 border border-accent/30 text-content-primary'
                    : 'hover:bg-surface-border/30 text-content-secondary'
                }`}
              >
                <div className="font-medium text-sm truncate">{c.title}</div>
                <div className="text-2xs text-content-tertiary mt-0.5">{formatRelative(c.last_message_at)}</div>
              </button>
            );
          })}
        </div>
      </aside>
    </>
  );
};

export default ConversationSidebar;
